import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import { triggerMilestoneRefresh } from 'ducks/projectMilestones'
import { mergeSetupChecklistWidgetSteps } from 'util/cashFlow/constants'
import { InvoiceEmail } from 'projectSections/sections/payments/cashFlowTransactions/requestPayment/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import restClient from 'restClient'
import { ChangeOrderType } from 'types/projects'
import {
  GetExpectedMilestonesResponseType,
  MilestoneOverrideType,
  PreviewEmailResponse,
  SetupChecklistResponse,
} from 'types/cashFlow'
import { useFeatureFlag } from 'util/featureFlags'
import { isCompletedChangeOrder } from 'util/changeOrder'

const restClientInstance = restClient(window.API_ROOT + '/api')

export const useGetIsCashFlowVisible = () => {
  return !!useFeatureFlag('cash_flow', 'on')
}

export const useGetCashFlowNavVisible = () => {
  const cashFlowIsVisible = useGetIsCashFlowVisible()
  const permissions = useSelector(permissionsSelectors.getPermissionsSetting)
  const cashflowOnboardingIsSubmitted = useSelector(orgSelectors.getBlueSnapOnboardingIsSubmitted)

  const showCashFlow = useMemo(() => {
    if (!cashFlowIsVisible) return false
    if (!cashflowOnboardingIsSubmitted) return true
    else {
      return (
        permissions?.cashflow_configuration?.view &&
        permissions?.cashflow_nav_configure?.view &&
        permissions?.cashflow_nav_invoices?.view &&
        permissions?.cashflow_nav_projects?.view &&
        permissions?.cashflow_onboarding?.view
      )
    }
  }, [cashFlowIsVisible, cashflowOnboardingIsSubmitted, permissions])

  return showCashFlow
}

const getCashFlowSetupChecklist = async (orgId: number): Promise<SetupChecklistResponse | undefined> => {
  const response = await restClientInstance('CUSTOM_GET', 'custom', {
    url: `orgs/${orgId}/cashflow/setup_checklist/`,
  })
  return response?.data
}

export const useCashflowSetupOutstanding = (): boolean => {
  const org = useSelector(orgSelectors.getOrg)
  const isCashflowActive = org?.cashflow_is_active
  const [hasOutstanding, setHasOutstanding] = useState(false)

  useEffect(() => {
    if (!org?.id || !isCashflowActive) {
      setHasOutstanding(false)
      return
    }

    let cancelled = false
    getCashFlowSetupChecklist(org.id)
      .then((data: SetupChecklistResponse | undefined) => {
        if (cancelled || !data) {
          return
        }
        const setupSteps = mergeSetupChecklistWidgetSteps(data)
        const completedCount = setupSteps.filter((step) => step.status === 'complete').length
        setHasOutstanding(setupSteps.length > 0 && completedCount < setupSteps.length)
      })
      .catch(() => {
        if (!cancelled) setHasOutstanding(false)
      })

    return () => {
      cancelled = true
    }
  }, [org?.id, isCashflowActive])

  return hasOutstanding
}

/**
 * Canonical helper for `POST orgs/:orgId/projects/:projectId/payments/next/`.
 *
 * All call sites must import from `util/cashFlow` — do not duplicate this
 * helper elsewhere (there used to be a second copy in `pages/cashFlow/utils.ts`
 * which drifted and has been removed).
 */
export const makeNextPaymentRequest = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  asDraft: boolean = true,
  limit_payment_number?: number,
  emailReminderStatus: 'active' | 'paused' = 'active',
  noInvoiceDocument: boolean = false,
  emailInfo: InvoiceEmail | null = null,
  // Set to true only for the proposal-acceptance auto-issuance flow
  // (IntegratedCheckoutWrapper). Any call from a pro-initiated Request Payment
  // dialog — milestone card, standalone payment, adhoc — must leave this false
  // so the pro's explicit send-email / generate-only choice wins over the
  // org-level Communications issue_invoice.enabled toggle.
  isProposalAcceptance: boolean = false
) => {
  return new Promise<{ payment_request_id: number; payment_url: string; invoice_id: number }>((resolve, reject) => {
    if (!orgId || !projectId || !systemUuid || !paymentOptionId) {
      reject('Invalid payment data')
    } else {
      restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/payments/next/`,
        data: {
          payment_option_id: paymentOptionId,
          system_uuid: systemUuid,
          as_draft: asDraft,
          payment_number: limit_payment_number,
          email_reminder_status: emailReminderStatus,
          no_invoice_document: noInvoiceDocument,
          email_info: emailInfo,
          is_proposal_acceptance: isProposalAcceptance,
        },
      })
        .then((response) => {
          resolve(response?.data)
        })
        .catch((err) => {
          reject(err)
        })
    }
  })
}

export const getExpectedMilestones = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  overrides?: MilestoneOverrideType[],
  pricePayable?: number,
  milestoneValidationOverridden?: boolean
): Promise<GetExpectedMilestonesResponseType> => {
  return new Promise((resolve, reject) => {
    if (!orgId || !projectId) {
      reject('Invalid payment data')
    } else {
      const data: Record<string, unknown> = {
        system_uuid: systemUuid,
        payment_option_id: paymentOptionId,
        overrides,
        price_payable: pricePayable,
      }
      if (milestoneValidationOverridden !== undefined) {
        data.milestone_validation_overridden = milestoneValidationOverridden
      }
      restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/projects/${projectId}/payments/`,
        data,
      })
        .then((response: GetExpectedMilestonesResponseType) => {
          resolve(response)
        })
        .catch((err) => {
          reject(err?.body?.message)
        })
    }
  })
}

export const cancelPaymentRequest = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  paymentRecordId: number,
  sendCancelNotification: boolean = true
) => {
  return new Promise((resolve, reject) => {
    if (!orgId) {
      reject('Invalid org')
      return
    }
    if (projectId) {
      if (!systemUuid) {
        reject('Invalid system uuid for project')
        return
      }
      if (!paymentOptionId) {
        reject('Invalid payment option id for project')
        return
      }
      if (!paymentRecordId) {
        reject('Invalid payment record id for project')
        return
      }
    }

    const url = projectId
      ? `orgs/${orgId}/projects/${projectId}/payments/${paymentRecordId}/cancel/`
      : `orgs/${orgId}/payments/${paymentRecordId}/cancel/`
    restClientInstance('CUSTOM_PUT', 'custom', {
      url,
      data: {
        payment_option_id: paymentOptionId,
        system_uuid: systemUuid,
        send_cancel_notification: sendCancelNotification,
      },
    })
      .then((response) => {
        resolve(true)
      })
      .catch((err) => {
        reject(err?.body?.message)
      })
  })
}

export const markPaymentRequestPaidOffline = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  paymentRequestId: number | null,
  paymentMethod: string,
  paymentRequestNumber: number,
  paymentReceivedDate: string
) => {
  return new Promise((resolve, reject) => {
    if (!orgId) {
      return reject('Invalid payment data')
    }
    if (!projectId && !paymentRequestId) {
      // For No project invoice
      return reject('Invalid payment data')
    }
    if (projectId && (!systemUuid || !paymentOptionId || !paymentMethod)) {
      // For invoices with project
      return reject('Invalid payment data')
    }

    const url = projectId
      ? `orgs/${orgId}/projects/${projectId}/payments/paid_offline/`
      : `orgs/${orgId}/payments/paid_offline/`

    restClientInstance('CUSTOM_PATCH', 'custom', {
      url,
      data: {
        payment_option_id: paymentOptionId,
        system_uuid: systemUuid,
        offline_payment_method: paymentMethod,
        payment_received_date: paymentReceivedDate,
        payment_number: paymentRequestNumber,
        payment_request_id: paymentRequestId,
      },
    })
      .then((response) => {
        resolve(true)
      })
      .catch((err) => {
        reject(err?.body?.message)
      })
  })
}

export const markPaymentRequestAsNotPaid = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  paymentRequestId: number | null
) => {
  return new Promise((resolve, reject) => {
    if (!orgId) {
      return reject('Invalid payment data')
    }
    if (projectId && (!systemUuid || !paymentOptionId || !paymentRequestId)) {
      return reject('Invalid payment data')
    }

    const url = `orgs/${orgId}/payments/mark_as_not_paid/`

    restClientInstance('CUSTOM_PATCH', 'custom', {
      url,
      data: {
        payment_option_id: paymentOptionId,
        system_uuid: systemUuid,
        payment_request_id: paymentRequestId,
        project_id: projectId,
      },
    })
      .then((response) => {
        resolve(true)
      })
      .catch((err) => {
        reject(err?.body?.message)
      })
  })
}

export const statusUpdate = (orgId: number, paymentRequestId: number | null, status: string) => {
  return new Promise((resolve, reject) => {
    if (!orgId) {
      return reject('Invalid payment data')
    }
    if (!paymentRequestId) {
      return reject('Invalid payment request')
    }

    const url = `orgs/${orgId}/payments/${paymentRequestId}/status_update/`

    restClientInstance('CUSTOM_PATCH', 'custom', {
      url,
      data: {
        status: status,
      },
    })
      .then((response) => {
        resolve(true)
      })
      .catch((err) => {
        const msg = err?.body?.message || 'An error occurred while updating the payment request status'
        reject(msg)
      })
  })
}

export const undoMarkPaymentRequestPaidOffline = (
  orgId: number,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  paymentRequestId: number | null
) => {
  return new Promise((resolve, reject) => {
    if (!orgId) {
      return reject('Invalid payment data')
    }
    if (projectId && (!systemUuid || !paymentOptionId || !paymentRequestId)) {
      return reject('Invalid payment data')
    }

    const url = projectId
      ? `orgs/${orgId}/projects/${projectId}/payments/paid_offline/undo/`
      : `orgs/${orgId}/payments/paid_offline/undo/`

    restClientInstance('CUSTOM_PATCH', 'custom', {
      url,
      data: {
        payment_option_id: paymentOptionId,
        system_uuid: systemUuid,
        payment_request_id: paymentRequestId,
      },
    })
      .then((response) => {
        resolve(true)
      })
      .catch((err) => {
        reject(err?.body?.message)
      })
  })
}

export const pauseEmailReminders = (orgId: number, paymentRequestId: number, remindersAreMuted: boolean) => {
  const endpoint = remindersAreMuted ? 'resume_reminders' : 'pause_reminders'
  return new Promise((resolve, reject) => {
    if (!orgId || !paymentRequestId) {
      reject('Invalid payment data')
    } else {
      restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${orgId}/payments/requests/${paymentRequestId}/${endpoint}/`,
      })
        .then((response) => {
          resolve(true)
        })
        .catch((err) => {
          reject(err?.body?.message)
        })
    }
  })
}

export const previewEmail = (orgId: number, invoiceId: number, template: string) => {
  return new Promise<PreviewEmailResponse>((resolve, reject) => {
    if (invoiceId) {
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/invoices/${invoiceId}/preview_email/?template=${template}`,
      })
        .then((res: { data: PreviewEmailResponse }) => {
          resolve(res?.data)
        })
        .catch((error) => {
          console.error(error.message)
          reject(error)
        })
    }
  })
}

export const previewOrgEmail = (orgId: number, template: string, amount: number, recipientFirstName: string) => {
  return new Promise<PreviewEmailResponse>((resolve, reject) => {
    if (orgId) {
      restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${orgId}/cashflow/org_email_template_preview/?template_name=${template}&amount=${amount}&recipient_first_name=${recipientFirstName}`,
      })
        .then((res: { data: PreviewEmailResponse }) => {
          resolve(res?.data)
        })
        .catch((error) => {
          console.error(error.message)
          reject(error)
        })
    }
  })
}

/**
 * Hook that monitors changes to the project's system and payment option selections
 * and triggers a recalculation of the cashflow when needed.
 *
 * @param project - The project object containing system_sold and payment_option_sold
 * @param form - The form object with mutators for marking fields as dirty
 * @returns A callback function to trigger recalculation if needed
 */
export const useRecalculateCashflowOnSaleChange = (project, form) => {
  const dispatch = useDispatch()
  const [initialSystemSoldUrl, setInitialSystemSoldUrl] = useState(project?.system_sold)
  const [initialPaymentOptionSoldUrl, setInitialPaymentOptionSoldUrl] = useState(project?.payment_option_sold)
  const [initialCompletedChangeOrderIds, setInitialCompletedChangeOrderIds] = useState<number[]>(() =>
    getCompletedChangeOrderIds(project?.change_orders)
  )
  const [shouldRecalculate, setShouldRecalculate] = useState(false)
  const pendingMilestoneRefresh = useRef(false)

  const orgHasCashFlow = useSelector(orgSelectors.getCashFlowIsActive)

  useEffect(() => {
    if (!orgHasCashFlow) return

    const completedIds = getCompletedChangeOrderIds(project?.change_orders)
    const hasNewCompletedChangeOrder =
      completedIds.length > initialCompletedChangeOrderIds.length ||
      completedIds.some((id) => !initialCompletedChangeOrderIds.includes(id))

    if (initialSystemSoldUrl && initialPaymentOptionSoldUrl) {
      if (
        initialSystemSoldUrl !== project?.system_sold ||
        initialPaymentOptionSoldUrl !== project?.payment_option_sold ||
        hasNewCompletedChangeOrder
      ) {
        setShouldRecalculate(true)
        pendingMilestoneRefresh.current = true
      }
    } else if (hasNewCompletedChangeOrder) {
      pendingMilestoneRefresh.current = true
      dispatch(triggerMilestoneRefresh())
    }
  }, [
    orgHasCashFlow,
    project?.system_sold,
    project?.payment_option_sold,
    project?.change_orders,
    initialSystemSoldUrl,
    initialPaymentOptionSoldUrl,
    initialCompletedChangeOrderIds,
    dispatch,
  ])

  // when a project that is already sold has their sold system or payment option changed, we need to recalculate the project
  // to ensure the cashflow milestones are updated
  useEffect(() => {
    if (shouldRecalculate) {
      if (form.mutators.markFieldAsDirty) {
        form.mutators.markFieldAsDirty('design')
      }
      window.editor.filter('type', 'OsSystem').forEach((system: any) => {
        window.Designer.requestSystemCalculations(system)
      }, this)
    }
  }, [shouldRecalculate])

  useEffect(() => {
    const onCalcsUpdated = () => {
      if (pendingMilestoneRefresh.current) {
        pendingMilestoneRefresh.current = false
        dispatch(triggerMilestoneRefresh())
      }
    }
    window.editor?.signals?.systemCalculationsUpdated?.add(onCalcsUpdated)
    return () => {
      window.editor?.signals?.systemCalculationsUpdated?.remove(onCalcsUpdated)
    }
  }, [dispatch])

  return null
}

const getCompletedChangeOrderIds = (changeOrders: ChangeOrderType[] | undefined): number[] => {
  return (changeOrders || []).filter(isCompletedChangeOrder).map((co) => co.id)
}
